import { useEffect, useState, type FormEvent } from 'react'

import {
  api,
  ErrorApi,
  type EntradaCotizacion,
  type Espera,
  type FuenteConsumo,
  type ItemOffgrid,
  type Opciones,
  type OrigenGuardado,
  type ResultadoCotizacion as Resultado,
} from '../api'
import { BarraGuardar } from './BarraGuardar'
import { DescuentoCliente } from './DescuentoCliente'
import { CampoComision } from './CampoComision'
import { CampoUtilidad, porcentaje } from './CampoUtilidad'
import { CampoInstalacionKwp } from './CampoInstalacionKwp'
import { CamposManuales } from './CamposManuales'
import { ETIQUETA_CATEGORIA, TARIFA_DE_CATEGORIA } from './categoriasCotizacion'
import { armarEntradaEstandar } from './entradaEstandar'
import { edicionMargenes } from './edicionMargenes'
import { EquiposAMano } from './EquiposAMano'
import { PartidasQuitadas } from './PartidasQuitadas'
import { leerEspera, OrigenConsumo, SupuestosEspera, type TextoEspera } from './OrigenConsumo'
import { ResultadoCotizacion } from './ResultadoCotizacion'

interface Props {
  clienteId: string
  /** La entrada de una version anterior, para partir de ella. */
  base?: EntradaCotizacion & OrigenGuardado
  consumoSugerido?: number | null
  /** La categoria del cliente, para que la tarifa por defecto sea la suya y no residencial. */
  categoriaCliente?: string
  alGuardar: (cotizacionId: string) => void
  alCancelar: () => void
  /** El asesor eligio "Off-grid": es otro formulario (sin consumo ni analisis financiero). */
  alElegirOffgrid?: () => void
}

export function NuevaCotizacion({ clienteId, base, consumoSugerido, categoriaCliente, alGuardar, alCancelar, alElegirOffgrid }: Props) {
  const [opciones, setOpciones] = useState<Opciones | null>(null)
  const [consumo, setConsumo] = useState(String(base?.consumo_kwh_mes ?? consumoSugerido ?? ''))
  const [zona, setZona] = useState(base?.zona ?? 'guayaquil')
  const [conBateria, setConBateria] = useState(base?.con_bateria ?? false)
  const [categoria, setCategoria] = useState(base?.categoria ?? TARIFA_DE_CATEGORIA[categoriaCliente ?? ''] ?? 'residencial')
  const [descuento, setDescuento] = useState(base ? String(base.descuento_pct * 100) : '0')
  const [tarifa, setTarifa] = useState(base?.tarifa_usd_kwh ? String(base.tarifa_usd_kwh) : '')
  const [tasa, setTasa] = useState(base?.tasa_descuento ? String(base.tasa_descuento * 100) : '')
  const [nota, setNota] = useState('')
  const [aMano, setAMano] = useState(Boolean(base?.equipos))
  const [panelM, setPanelM] = useState<ItemOffgrid>(base?.equipos?.paneles ?? { codigo: '', cantidad: 8 })
  const [inversorM, setInversorM] = useState<ItemOffgrid>(base?.equipos?.inversor ?? { codigo: '', cantidad: 1 })
  const [bateriaM, setBateriaM] = useState<ItemOffgrid>(base?.equipos?.bateria ?? { codigo: '', cantidad: 1 })
  const [excluir, setExcluir] = useState<string[]>(base?.excluir ?? [])
  const [costos, setCostos] = useState<Record<string, number>>(base?.costos ?? {})
  const [margenes, setMargenes] = useState<Record<string, number>>(base?.margenes ?? {})
  const [materiales, setMateriales] = useState(base?.materiales_usd != null ? String(base.materiales_usd) : '')
  const [instalacion, setInstalacion] = useState(base?.instalacion_usd != null ? String(base.instalacion_usd) : '')
  const [utilidad, setUtilidad] = useState(base?.margen_pct != null ? String(porcentaje(base.margen_pct)) : '')
  const [comision, setComision] = useState(base?.comision_pct ? String(porcentaje(base.comision_pct)) : '')
  const [fuente, setFuente] = useState<FuenteConsumo>(base?.origen_consumo?.fuente ?? 'manual')
  const [espera, setEspera] = useState<TextoEspera>({ tarifa: String((base?.espera?.subida_anual_tarifa ?? 0) * 100), precio: String((base?.espera?.subida_anual_precio ?? 0) * 100) })
  const [calculada, setCalculada] = useState<{ entrada: EntradaCotizacion; espera: Espera; resultado: Resultado } | null>(null)
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState<'' | 'calculando' | 'guardando'>('')

  useEffect(() => {
    api
      .opciones()
      .then((o) => {
        setOpciones(o)
        setUtilidad((u) => u || (o.margen_por_defecto === undefined ? '' : String(porcentaje(o.margen_por_defecto))))
        // Sin version anterior, cada lista parte de su primera opcion.
        const e = o.equipos
        if (!e) return
        setPanelM((p) => (p.codigo ? p : { ...p, codigo: e.paneles[0]!.codigo }))
        setInversorM((p) => (p.codigo ? p : { ...p, codigo: e.inversores[0]!.codigo }))
        setBateriaM((p) => (p.codigo ? p : { ...p, codigo: e.baterias[0]!.codigo }))
      })
      .catch((err) => setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar.'))
  }, [])

  // Lo que se retoca a mano (costos, utilidades y campos de materiales/instalacion), junto para
  // pasar el valor NUEVO a `correr` sin esperar al estado de React.
  const manuales = { costos, materiales, instalacion, margenes }
  type Manuales = typeof manuales

  const armarEntrada = (quitadas: string[], m: Manuales) =>
    armarEntradaEstandar(
      { consumo, zona, conBateria, categoria, descuento, tarifa, tasa, aMano, panel: panelM, inversor: inversorM, bateria: bateriaM, utilidad, comision, ...m },
      quitadas,
      opciones,
    )

  const calcular = (e: FormEvent) => {
    e.preventDefault()
    void correr(excluir, manuales)
  }

  // Quitar o devolver una partida, o cambiar un costo, recalcula al instante.
  const cambiarQuitadas = (nuevas: string[]) => {
    setExcluir(nuevas)
    void correr(nuevas, manuales)
  }
  const quitar = (codigo: string) => cambiarQuitadas([...excluir, codigo])
  const restaurar = (codigo: string) => cambiarQuitadas(excluir.filter((c) => c !== codigo))
  const cambiarManuales = (nuevos: Manuales) => {
    setCostos(nuevos.costos)
    setMateriales(nuevos.materiales)
    setInstalacion(nuevos.instalacion)
    setMargenes(nuevos.margenes)
    void correr(excluir, nuevos)
  }
  const edicionUtilidades = edicionMargenes(margenes, (nuevos) => cambiarManuales({ ...manuales, margenes: nuevos }))
  const edicionCostos = {
    cambiados: costos,
    alCambiar: (codigo: string, nuevo: number) => {
      // Si esa partida esta fijada en su campo ("1 proyecto", no por kWp), la celda cambia ESE campo.
      if (codigo === 'materiales_electricos' && materiales.trim() !== '') cambiarManuales({ ...manuales, materiales: String(nuevo) })
      else if (codigo === 'instalacion' && instalacion.trim() !== '') cambiarManuales({ ...manuales, instalacion: String(nuevo) })
      else cambiarManuales({ ...manuales, costos: { ...costos, [codigo]: nuevo } })
    },
    alRestablecer: (codigo: string) =>
      cambiarManuales({ ...manuales, costos: Object.fromEntries(Object.entries(costos).filter(([c]) => c !== codigo)) }),
  }

  const correr = async (quitadas: string[], m: Manuales) => {
    const entrada = armarEntrada(quitadas, m)
    const supuestos = leerEspera(espera)
    if (typeof entrada === 'string' || typeof supuestos === 'string') {
      setError(typeof entrada === 'string' ? entrada : (supuestos as string))
      return
    }
    setError('')
    setOcupado('calculando')
    try {
      const { resultado } = await api.calcular(entrada, 'estandar', { clienteId, fuente, espera: supuestos })
      // Con proyeccion, el consumo lo pone el servidor: se muestra el que uso.
      if (resultado.consumo_usado) setConsumo(String(resultado.consumo_usado.kwh_mes))
      setCalculada({ entrada, espera: supuestos, resultado })
    } catch (err) {
      setCalculada(null)
      setError(err instanceof ErrorApi ? err.message : 'No se pudo calcular.')
    } finally {
      setOcupado('')
    }
  }

  const guardar = async () => {
    if (!calculada) return
    setOcupado('guardando')
    try {
      const { cotizacion } = await api.guardarCotizacion(clienteId, calculada.entrada, nota, 'estandar', { fuente, espera: calculada.espera })
      alGuardar(cotizacion.id)
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.')
      setOcupado('')
    }
  }

  // Cualquier cambio invalida el resultado en pantalla: no se guarda lo que nadie pidio.
  const cambia = <T,>(fijar: (v: T) => void) => (v: T) => {
    fijar(v)
    setCalculada(null)
  }

  return (
    <section>
      <div className="encabezado">
        <div>
          <h1>{base ? 'Nueva versión' : 'Nueva cotización'}</h1>
          <p className="suave">Calcula, ajusta lo que haga falta y guarda cuando esté lista. Nada se envía al cliente.</p>
        </div>
        <button className="secundario" onClick={alCancelar}>
          Cancelar
        </button>
      </div>

      <form className="tarjeta formulario-cotizacion" onSubmit={calcular}>
        <OrigenConsumo
          clienteId={clienteId}
          industrial={false}
          fuente={fuente}
          alCambiarFuente={cambia(setFuente)}
          alProyectar={(kwh) => cambia(setConsumo)(String(kwh))}
        />
        <label>
          Consumo mensual (kWh)
          <input
            value={consumo}
            onChange={(e) => cambia(setConsumo)(e.target.value)}
            inputMode="decimal"
            required
            readOnly={fuente === 'proyeccion'}
          />
          {fuente === 'proyeccion' && <small className="ayuda">Proyectado. Para escribirlo, elige «Escrito a mano».</small>}
        </label>
        <label>
          Zona
          <select value={zona} onChange={(e) => cambia(setZona)(e.target.value)}>
            {(opciones?.zonas ?? [zona]).map((z) => (
              <option key={z} value={z}>
                {z.charAt(0).toUpperCase() + z.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Sistema
          <select
            value={conBateria ? 'si' : 'no'}
            onChange={(e) => {
              if (e.target.value === 'offgrid') alElegirOffgrid?.()
              else cambia((v) => setConBateria(v === 'si'))(e.target.value)
            }}
          >
            <option value="no">On-grid</option>
            <option value="si">Híbrido con batería</option>
            {alElegirOffgrid && <option value="offgrid">Off-grid</option>}
          </select>
        </label>
        <EquiposAMano
          activo={aMano}
          alCambiarActivo={cambia(setAMano)}
          equipos={opciones?.equipos}
          conBateria={conBateria}
          panel={panelM}
          alCambiarPanel={cambia(setPanelM)}
          inversor={inversorM}
          alCambiarInversor={cambia(setInversorM)}
          bateria={bateriaM}
          alCambiarBateria={cambia(setBateriaM)}
        />
        <label>
          Tarifa del cliente
          <select value={categoria} onChange={(e) => cambia(setCategoria)(e.target.value)}>
            {(opciones?.categorias ?? [categoria]).map((c) => (
              <option key={c} value={c}>
                {ETIQUETA_CATEGORIA[c] ?? c}
              </option>
            ))}
          </select>
        </label>
        <label>
          Tarifa manual (USD/kWh)
          <input value={tarifa} onChange={(e) => cambia(setTarifa)(e.target.value)} inputMode="decimal" placeholder="Vacío = pliego oficial" />
          <small className="ayuda">Si la pones, se usa como tarifa plana.</small>
        </label>
        <CamposManuales
          materiales={materiales}
          alCambiarMateriales={cambia(setMateriales)}
          instalacion={instalacion}
          alCambiarInstalacion={cambia(setInstalacion)}
          materialesQuitados={excluir.includes('materiales_electricos')}
          instalacionQuitada={excluir.includes('instalacion')}
          placeholderInstalacion="Vacío = por kWp, como siempre"
        />
        <CampoInstalacionKwp
          costos={costos}
          alCambiar={cambia(setCostos)}
          tarifas={opciones?.instalacion_usd_kwp}
          conBateria={conBateria}
          bloqueado={instalacion.trim() !== '' || excluir.includes('instalacion')}
        />
        <CampoUtilidad valor={utilidad} alCambiar={cambia(setUtilidad)} porDefecto={opciones?.margen_por_defecto} />
        <CampoComision valor={comision} alCambiar={cambia(setComision)} />
        <label>
          Tasa de descuento (%)
          <input
            value={tasa}
            onChange={(e) => cambia(setTasa)(e.target.value)}
            inputMode="decimal"
            placeholder={opciones ? `${(opciones.tasa_descuento_cliente * 100).toFixed(2)} por defecto` : ''}
          />
        </label>
        <DescuentoCliente valor={descuento} alCambiar={cambia(setDescuento)} maximo={opciones?.descuento_maximo_pct} />
        <SupuestosEspera valor={espera} alCambiar={cambia(setEspera)} />
        <div className="accion-formulario">
          <button className="primario" disabled={ocupado !== ''}>
            {ocupado === 'calculando' ? 'Calculando…' : calculada ? 'Recalcular' : 'Calcular'}
          </button>
        </div>
      </form>

      {error && <p className="error">{error}</p>}

      {calculada && (
        <>
          <PartidasQuitadas excluir={excluir} alRestaurar={restaurar} />
          <ResultadoCotizacion resultado={calculada.resultado} alQuitar={quitar} costos={edicionCostos} margenes={edicionUtilidades} />
          <BarraGuardar nota={nota} alCambiarNota={setNota} alGuardar={guardar} guardando={ocupado === 'guardando'} deshabilitado={ocupado !== ''} />
        </>
      )}
    </section>
  )
}
